/**
 * Helper PURO dello switch "Sincronizza con il calendario" del drawer Attività/Nota.
 *
 * ECCEZIONE FUNZIONALE B1 del redesign (dichiarata in
 * MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md, "Risoluzione dei gap"):
 * fino ad oggi lo switch e l'init di `syncOptIn` guardavano SOLO Google
 * (`google.enabled && google.calendarId`), quindi un assegnatario Microsoft-only non
 * poteva in alcun modo attivare la sync (lo switch era proprio assente e `syncOptIn`
 * restava false → `syncMicrosoftEvent` usciva subito con `skipped`). Qui il provider
 * Microsoft diventa una sorgente valida allo stesso titolo di Google.
 *
 * Regole:
 * - Google "pronto" = sync attività attiva E calendario configurato (invariato: senza
 *   `calendarId` `syncGoogleEvent` esce comunque `skipped`).
 * - Microsoft "pronto" = sync attività attiva E account collegato E calendario configurato
 *   (simmetrico a Google: senza il collegamento Graph non c'è mailbox su cui scrivere e
 *   senza `microsoft_sync_activities_calendar_id` `syncMicrosoftEvent` non può scrivere —
 *   proporre lo switch sarebbe una promessa non mantenuta).
 * - Entrambi pronti → label neutra (l'evento finisce su tutti e due i calendari).
 * - Nessuno pronto → switch NON reso: al suo posto il callout informativo (vista 4 del
 *   mockup), e `syncOptIn` parte spento.
 */

export interface SyncSwitchGoogleState {
  enabled: boolean;
  calendarId?: string | null;
}

export interface SyncSwitchMicrosoftState {
  enabled: boolean;
  connected: boolean;
  calendarId?: string | null;
}

export interface SyncSwitchResolution {
  /** false = niente switch, si mostra il callout "Sincronizzazione non attiva". */
  visible: boolean;
  /** Label dello switch, provider-aware. Stringa vuota quando `visible` è false. */
  label: string;
  /** Valore iniziale di `syncOptIn` (opt-out: pre-spuntato quando la sync è possibile). */
  defaultChecked: boolean;
}

const LABEL_GOOGLE = 'Sincronizza con Google Calendar';
const LABEL_MICROSOFT = 'Sincronizza con Outlook';
const LABEL_BOTH = 'Sincronizza con il calendario';

export function resolveSyncSwitch(
  google: SyncSwitchGoogleState,
  microsoft: SyncSwitchMicrosoftState
): SyncSwitchResolution {
  const googleReady = !!google?.enabled && !!google?.calendarId;
  const microsoftReady = !!microsoft?.enabled && !!microsoft?.connected && !!microsoft?.calendarId;

  if (!googleReady && !microsoftReady) {
    return { visible: false, label: '', defaultChecked: false };
  }

  const label = googleReady && microsoftReady ? LABEL_BOTH : googleReady ? LABEL_GOOGLE : LABEL_MICROSOFT;
  return { visible: true, label, defaultChecked: true };
}
